import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import SideBar, { APPS } from './components/SideBar';
import GlobalSearch from './components/GlobalSearch';
import ToolFrame from './pages/ToolFrame';
import UsersManager from './pages/UsersManager';
import Home from './pages/Home';
import Tools from './pages/Tools';
import QuoteBuilder from './pages/QuoteBuilder';
import NotesBoard from './pages/NotesBoard';
import ObjectivesT4 from './pages/ObjectivesT4';
import AssistantMascot from './components/AssistantMascot';
import PrivateChat from './pages/PrivateChat';
import ChatNotifications from './components/ChatNotifications';
import './styles/global.css';
import './styles/theme.css';

const THEME_KEY = 'ams_theme';

function getSavedTheme() {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === 'light' || saved === 'dark') return saved;

  const cookieTheme = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${THEME_KEY}=`))
    ?.split('=')[1];
  return cookieTheme === 'light' ? 'light' : 'dark';
}

function AuthenticatedApp({ user, setUser, isTableauUser, theme, onThemeChange }) {
  const location = useLocation();
  const login = String(user?.login || user?.user || user?.username || '').trim().toLowerCase();
  const tools = [
    { path: '/backup', url: 'https://backup.amservices.eu' },
    { path: '/planning', url: isTableauUser ? 'https://planning.amservices.eu/affichage' : 'https://planning.amservices.eu' },
    { path: '/appels', url: isTableauUser ? 'https://appel.amservices.eu/affichage' : 'https://appel.amservices.eu' },
    { path: '/suivi', url: 'https://suivi.amservices.eu/suivi' },
    { path: '/cdr', url: 'https://cdr.amservices.eu' },
    { path: '/stock', url: 'https://stock.amservices.eu' },
    { path: '/kanban', url: 'https://kanban.amservices.eu/' },
    { path: '/client', url: 'https://client.amservices.eu/' },
    { path: '/comcom', url: 'https://comcom.client.amservices.eu/' },
    { path: '/sms', url: 'https://sms.amservices.eu/' },
    ...(login === 'aurelien' ? [{ path: '/kanban_aurelien', url: 'https://aurelien.kanban.amservices.eu/' }] : []),
    ...(login === 'aurelien' ? [{ path: '/commercial', url: 'https://commercial.amservices.eu/' }] : []),
    ...(['aymeric', 'aurelien'].includes(login) ? [{ path: '/kanban_aymeric', url: 'https://aymeric.kanban.amservices.eu/' }] : []),
  ];
  const activeTool = tools.find((tool) => tool.path === location.pathname);
  const activeToolPath = activeTool?.path || '';
  const [openedTools, setOpenedTools] = useState(() => activeTool ? [activeTool.path] : []);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchApps = APPS.filter(app => (!app.masterOnly || ['aurelien', 'aymeric'].includes(login)) && (!app.allowedLogins || app.allowedLogins.includes(login)));

  useEffect(() => {
    if (isTableauUser) return undefined;
    const shortcut = event => {
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (!event.repeat) setSearchOpen(value => !value);
      }
    };
    const relay = () => setSearchOpen(true);
    window.addEventListener('keydown', shortcut);
    window.addEventListener('ams-open-global-search', relay);
    return () => { window.removeEventListener('keydown', shortcut); window.removeEventListener('ams-open-global-search', relay); };
  }, [isTableauUser]);

  useEffect(() => {
    if (!activeToolPath) return;
    setOpenedTools((current) => [activeToolPath, ...current.filter((path) => path !== activeToolPath)].slice(0, 4));
  }, [activeToolPath]);

  return (
    <div className={`admin-page ${isTableauUser ? 'tableau-admin-page' : ''}`} style={{ display: 'flex', padding: 0, height: '100vh', width: '100vw', overflow: 'hidden' }}>
      <SideBar user={user} setUser={setUser} theme={theme} onThemeChange={onThemeChange} onSearch={isTableauUser ? undefined : () => setSearchOpen(true)} />
      {!isTableauUser && <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} user={user} apps={searchApps} />}

      <div className={`iframe-wrapper ${location.pathname === '/notes-board' ? 'notes-board-wrapper' : ''}`}>
        {tools.filter((tool) =>
          openedTools.includes(tool.path) &&
          (!isTableauUser || tool.path !== '/appels' || activeTool?.path === tool.path)
        ).map((tool) => (
          <div className={`cached-tool-pane ${activeTool?.path === tool.path ? 'active' : ''}`} key={tool.path}>
            <ToolFrame url={tool.url} theme={theme} searchSelection={activeTool?.path === tool.path ? location.state?.globalSearch : null} />
          </div>
        ))}

        {!activeTool && (
          <Routes>
            <Route path="/home" element={isTableauUser ? <Navigate to="/planning" replace /> : <Home user={user} />} />
            <Route
              path="/users"
              element={['aurelien', 'aymeric'].includes(login)
                ? <UsersManager currentUser={user} />
                : <Navigate to="/home" replace />}
            />
            <Route path="/outils" element={isTableauUser ? <Navigate to="/planning" replace /> : <Tools />} />
            <Route path="/devis" element={['aymeric', 'aurelien'].includes(login) ? <QuoteBuilder user={user} /> : <Navigate to="/home" replace />} />
            <Route path="/notes-board" element={<NotesBoard theme={theme} user={user} onThemeChange={onThemeChange} />} />
            <Route path="/chat-prive" element={isTableauUser ? <Navigate to="/planning" replace /> : <PrivateChat />} />
            <Route path="/objectifs-t4" element={<ObjectivesT4 compact={isTableauUser} canEditObjectives={login === 'aurelien'} />} />
            <Route path="*" element={<Navigate to={isTableauUser ? "/planning" : "/home"} replace />} />
          </Routes>
        )}
      </div>

      {!isTableauUser && <AssistantMascot user={user} />}
      {!isTableauUser && <ChatNotifications user={user} />}
    </div>
  );
}

function App() {
  const [theme, setTheme] = useState(getSavedTheme);
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('am_user');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);

    const domain = window.location.hostname.endsWith('.amservices.eu')
      ? '; Domain=.amservices.eu'
      : '';
    document.cookie = `${THEME_KEY}=${theme}; Path=/; Max-Age=31536000; SameSite=Lax${domain}`;
  }, [theme]);

  const toggleTheme = () => setTheme((current) => current === 'light' ? 'dark' : 'light');

  if (!user) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<Login setUser={setUser} theme={theme} onToggleTheme={toggleTheme} />} />
        </Routes>
      </Router>
    );
  }

  const isTableauUser = String(user?.login || user?.username || '').toLowerCase() === 'tableau';

  return (
    <Router>
      <AuthenticatedApp
        user={user}
        setUser={setUser}
        isTableauUser={isTableauUser}
        theme={theme}
        onThemeChange={setTheme}
      />
    </Router>
  );
}

export default App;
